/**
 * First Interviews — everyone in an opening-interview stage (First Interview
 * OR Personal Interview) who has not been invited yet.
 *
 * The one section that works per ROLE rather than per candidate: a first round
 * invites its whole shortlist with the same email, so the interaction is select →
 * review → one confirmation, not eight identical conversations. Selection state
 * lives here (per role) and the review happens in BulkInviteModal.
 */
import { useMemo, useState } from 'react';
import { CandidateChips } from './CandidateChips';
import { Accordion } from '@finn-auto/design-system/atoms/accordion';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { AlertTriangle, Link2Off, Mail } from 'lucide-react';
import type {
  FirstInterviewCandidate,
  FirstInterviewStageSetup,
  RoleBoard,
} from '../../types/taActionBoard';
import { COLORS, fmtDaysAgo } from './format';
import { FixInterviewLink } from './FixInterviewLink';
import { HandledChip } from './CandidateCard';
import { HiddenNote } from './HiddenNote';
import { RolePipeline } from './RolePipeline';
import { PhoneChip } from './PhoneChip';
import { RoleAccordion, SummaryChip } from './RoleAccordion';
import { useFocusCard, type FocusTarget } from './useFocusCard';
import { useBoardAvailability } from './useBoardAvailability';
import { AvailabilityNote, AvailabilityPill } from '../../components/interviewAvailability/AvailabilityLight';

export interface FirstInterviewsTabProps {
  roles: RoleBoard[];
  /** Whose board is on screen — the shared pipeline strip's Lever retry needs it. */
  boardEmail: string;
  /** Reload the board after that retry. */
  onChanged?: () => void;
  /** opportunityId → "Invite sent" … — cards render muted and unselectable. */
  handled: Record<string, string>;
  /** Candidate the Overdue tab sent us to — expand their role and scroll to them. */
  focus?: FocusTarget | null;
  /** Open the review-and-confirm modal for one role's selected candidates. */
  onBulkInvite: (role: RoleBoard, candidates: FirstInterviewCandidate[]) => void;
  /** Open the ⏰ SLA tab — where an already-invited candidate is actually chased. */
  onGoToSla?: () => void;
}

export function FirstInterviewsTab({
  roles,
  boardEmail,
  onChanged,
  handled,
  focus = null,
  onBulkInvite,
  onGoToSla,
}: FirstInterviewsTabProps) {
  const withCandidates = roles.filter((r) => (r.firstInterviews?.candidates.length ?? 0) > 0);
  const firstOpen = withCandidates[0]?.postingId;
  const [open, setOpen] = useState<string[]>(firstOpen ? [firstOpen] : []);
  useFocusCard(focus, setOpen);
  const { data: availability } = useBoardAvailability(roles);

  const waiting = withCandidates.reduce((n, r) => n + (r.firstInterviews?.candidates.length ?? 0), 0);
  // Stages that cannot produce a usable invitation at all. Counted across roles
  // because it is a setup problem, and the fix is on the Interview Links page.
  const brokenStages = withCandidates.flatMap((r) =>
    (r.firstInterviews?.stages || []).filter((s) => !s.linkFound)
  );
  const unauthored = withCandidates.flatMap((r) =>
    (r.firstInterviews?.stages || []).filter((s) => s.linkFound && !s.templateAuthored)
  );
  const historyBroken = withCandidates.some((r) => r.firstInterviews?.historyUnavailable);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <SummaryChip label={`${waiting} waiting for an invite`} bg={COLORS.orangeBg} text={COLORS.orangeText} />
        <SummaryChip
          label={`${brokenStages.length} stage${brokenStages.length === 1 ? '' : 's'} without an interview link`}
          bg={COLORS.redBg}
          text={COLORS.redText}
        />
        <SummaryChip
          label={`${unauthored.length} stages on the generic template`}
          bg={COLORS.cotton}
          text="#484848"
        />
      </div>

      {historyBroken && (
        <div
          className="flex items-start gap-2 rounded-xl px-4 py-3 text-sm"
          style={{ backgroundColor: COLORS.redBg, color: COLORS.redText }}
          role="status"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            The board could not read what has already been sent, so this list is{' '}
            <strong>not filtered</strong> — someone here may already have their invitation. Check each
            candidate in Lever before sending, or refresh the board.
          </span>
        </div>
      )}

      <Accordion type="multiple" value={open} onValueChange={setOpen} className="space-y-3">
        {roles.map((role) => {
          const n = role.firstInterviews?.candidates.length ?? 0;
          const scheduled = role.firstInterviews?.scheduled?.length ?? 0;
          return (
            <RoleAccordion
              key={role.postingId}
              role={role}
              // A role with nobody left to invite must still open: everyone already invited,
              // booked or arranged is in the collapsed list inside, and so is the footnote.
              hasOpenItems={n > 0 || scheduled > 0}
              chip={{ label: `${n} to invite`, bg: COLORS.orangeBg, text: COLORS.orangeText }}
              aside={
                availability[role.postingId] ? (
                  <>
                    <AvailabilityPill
                      stage={availability[role.postingId]?.first}
                      round="First"
                      windowEnd={availability[role.postingId]?.windowEnd ?? ''}
                    />
                    <AvailabilityPill
                      stage={availability[role.postingId]?.personal}
                      round="Personal"
                      windowEnd={availability[role.postingId]?.windowEnd ?? ''}
                    />
                  </>
                ) : null
              }
            >
              {(['first', 'personal'] as const).map((key) => (
                <AvailabilityNote
                  key={key}
                  stage={availability[role.postingId]?.[key]}
                  round={key === 'first' ? 'First' : 'Personal'}
                  windowEnd={availability[role.postingId]?.windowEnd ?? ''}
                />
              ))}
              <RoleFirstInterviews
                role={role}
                boardEmail={boardEmail}
                onChanged={onChanged}
                handled={handled}
                highlightId={focus?.opportunityId ?? null}
                onBulkInvite={onBulkInvite}
                onGoToSla={onGoToSla}
              />
            </RoleAccordion>
          );
        })}
      </Accordion>
    </div>
  );
}

/**
 * One role's batch, grouped by stage.
 *
 * Grouped rather than one flat list because the stage is what decides which
 * template and which interviewers a candidate gets — a batch that silently mixed
 * First and Personal Interview candidates would send two different emails under
 * one "Invite 8 candidates" button.
 */
function RoleFirstInterviews({
  role,
  boardEmail,
  onChanged,
  handled,
  highlightId = null,
  onBulkInvite,
  onGoToSla,
}: {
  role: RoleBoard;
  boardEmail: string;
  onChanged?: () => void;
  handled: Record<string, string>;
  /** Candidate arrived at from the Overdue tab — ringed so the landing is visible. */
  highlightId?: string | null;
  onBulkInvite: (role: RoleBoard, candidates: FirstInterviewCandidate[]) => void;
  /** Open the ⏰ SLA tab — where an already-invited candidate is actually chased. */
  onGoToSla?: () => void;
}) {
  const section = role.firstInterviews;
  const [selected, setSelected] = useState<Set<string>>(new Set());

  /** In this role's opening stages, invited already, still not booked — see the engine. */
  const hiddenCount = section?.alreadyInvited ?? 0;

  const byStage = useMemo(() => {
    const stages = section?.stages || [];
    return stages.map((stage) => ({
      stage,
      candidates: (section?.candidates || []).filter((c) => c.stageId === stage.stageId),
    }));
  }, [section]);

  if (!section || section.candidates.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-300">
          Nothing open — everyone in the first round has their invitation.
        </p>
        {/* …which is true and yet not the whole story: the people it is true ABOUT are right
            here, with what became of each invitation. Without the list the sentence above reads
            as "nobody is waiting" on exactly the role where someone is. */}
        <RolePipeline role={role} boardEmail={boardEmail} onChanged={onChanged} />
        <HiddenNote count={hiddenCount} onGoToSla={onGoToSla} />
      </div>
    );
  }

  const toggle = (opportunityId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(opportunityId)) next.delete(opportunityId);
      else next.add(opportunityId);
      return next;
    });

  return (
    <div className="space-y-4">
      {byStage.map(({ stage, candidates }) => {
        // Already-handled candidates are never part of a batch: their invite has
        // gone (or is in its countdown) and the board is only waiting for the
        // next refresh to drop them.
        const selectable = candidates.filter((c) => !handled[c.opportunityId]);
        const chosen = selectable.filter((c) => selected.has(c.opportunityId));
        const allChosen = selectable.length > 0 && chosen.length === selectable.length;
        return (
          <div key={stage.stageId} className="space-y-2 rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
            <StageHeader
              stage={stage}
              postingId={role.postingId}
              selectableCount={selectable.length}
              allChosen={allChosen}
              onToggleAll={() =>
                setSelected((prev) => {
                  const next = new Set(prev);
                  for (const c of selectable) {
                    if (allChosen) next.delete(c.opportunityId);
                    else next.add(c.opportunityId);
                  }
                  return next;
                })
              }
            />

            {candidates.map((candidate) => (
              <CandidateRow
                key={candidate.opportunityId}
                candidate={candidate}
                highlighted={highlightId === candidate.opportunityId}
                handledLabel={handled[candidate.opportunityId] || null}
                checked={selected.has(candidate.opportunityId)}
                onToggle={() => toggle(candidate.opportunityId)}
              />
            ))}

            <div className="flex items-center justify-between gap-3 pt-1">
              <span className="text-xs text-gray-500">
                {chosen.length === 0
                  ? 'Select the candidates to invite'
                  : `${chosen.length} of ${selectable.length} selected`}
              </span>
              <Button
                size="sm"
                disabled={chosen.length === 0}
                onClick={() => onBulkInvite(role, chosen)}
                className="rounded-full px-4"
                title="Draft every selected invitation and check the values before anything is sent"
              >
                <Mail className="mr-1.5 h-3.5 w-3.5" />
                Review &amp; invite {chosen.length > 0 ? chosen.length : ''}
              </Button>
            </div>
          </div>
        );
      })}

      {/*
        LAST in the role card, not above the lists, and in this order: the work, then where
        everyone else went, then the one-line pointer for chasing. Both are about people who are
        NOT on the open list — on top they pushed the actual work down and read as an alert about
        the candidates below them.
      */}
      <RolePipeline role={role} boardEmail={boardEmail} onChanged={onChanged} />
      <HiddenNote count={hiddenCount} onGoToSla={onGoToSla} />
    </div>
  );
}

/**
 * What the chip says when a stage has no governing link.
 *
 * One label for every case used to make the real misconfigurations — a link switched off, and a
 * link created on a neighbouring stage — read exactly like "nobody has set one up". That is the loop
 * the reported bug sat in: the recruiter DID set one up and the tab still said no.
 *
 * There is no 'expired' label any more: a link no longer dies `expiryDays` after it was created —
 * that is each candidate's own booking window. A payload cached before that change can still carry
 * 'expired'; `missingKey` reads it as 'none' rather than rendering an empty chip.
 */
const LINK_MISSING_LABEL: Record<string, string> = {
  none: 'no interview link',
  'other-stage': 'link is on another stage',
  inactive: 'interview link closed',
};

const LINK_MISSING_HELP: Record<string, string> = {
  none: 'Without an interview link there is no booking URL to put in the email. Set one up on the Interview Links page.',
  'other-stage':
    'This role has interview links, but none for this stage — check which stage the link was created against on the Interview Links page.',
  inactive:
    'A link exists for this stage but is closed. Reopen it on the Interview Links page.',
};

const missingKey = (reason: string | null | undefined) =>
  reason && LINK_MISSING_LABEL[reason] ? reason : 'none';

/**
 * Stage name, its interview-link setup, and select-all for the stage.
 *
 * **Select all leads the row.** It sat far right, past the stage name, the
 * counts and the setup chips, and was reported as invisible — so a batch of
 * twelve was ticked one candidate at a time by someone who never saw the
 * control built for exactly that. It is the first thing in the header now,
 * directly above the column of per-candidate checkboxes it operates on, which
 * is also where the eye already is.
 */
function StageHeader({
  stage,
  postingId,
  selectableCount,
  allChosen,
  onToggleAll,
}: {
  stage: FirstInterviewStageSetup;
  postingId: string;
  selectableCount: number;
  allChosen: boolean;
  onToggleAll: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 pb-1">
      <div className="flex min-w-0 items-center gap-2">
        {selectableCount > 0 && (
          <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs font-medium text-gray-600">
            <Checkbox
              checked={allChosen}
              onCheckedChange={onToggleAll}
              aria-label={`Select all ${stage.stageName} candidates`}
            />
            Select all {selectableCount}
          </label>
        )}
        <span className="text-sm font-semibold text-gray-900">{stage.stageName}</span>
        <span className="text-xs text-gray-500">
          {stage.candidateCount} waiting
          {stage.minutes ? ` · ${stage.minutes} min` : ''}
          {stage.meetingType ? ` · ${stage.meetingType}` : ''}
          {stage.interviewers.length > 0 ? ` · ${stage.interviewers.length} interviewer(s)` : ''}
          {/* Several live links (one per interviewer): the invite modal offers a picker. */}
          {(stage.linkCount ?? 0) > 1 ? ` · ${stage.linkCount} links — pick one when inviting` : ''}
        </span>
        {!stage.linkFound ? (
          <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
            style={{ backgroundColor: COLORS.redBg, color: COLORS.redText }}
            title={LINK_MISSING_HELP[missingKey(stage.linkMissing)]}
          >
            <Link2Off className="h-3 w-3" /> {LINK_MISSING_LABEL[missingKey(stage.linkMissing)]}
          </span>
        ) : !stage.templateAuthored ? (
          <span
            className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500"
            title="Nobody has written this stage's invite email — the generic library text will be used. Edit it on the Interview Links page."
          >
            generic template
          </span>
        ) : null}
      </div>
      {/*
        ALWAYS rendered, whether or not the stage has a link. Every warning above
        ends in "fix it on the Interview Links page", and until now that was a
        sentence rather than a route — the recruiter had to go and find the right
        role's right stage themselves. With no link it opens the create form for
        this exact role and stage.
      */}
      <FixInterviewLink
        linkId={stage.linkId}
        postingId={postingId}
        stageId={stage.stageId}
        stageName={stage.stageName}
        className="inline-flex shrink-0 items-center gap-1 text-xs text-blue-600 hover:underline"
      />
    </div>
  );
}

/** One candidate: checkbox, name, how long they have waited, phone, Lever link. */
function CandidateRow({
  candidate,
  highlighted = false,
  handledLabel,
  checked,
  onToggle,
}: {
  candidate: FirstInterviewCandidate;
  /** Arrived at from the Overdue tab — ringed, like a focused feedback card. */
  highlighted?: boolean;
  handledLabel: string | null;
  checked: boolean;
  onToggle: () => void;
}) {
  const stale = candidate.waitingDays >= 3;
  return (
    <div
      // Landing target for the Overdue tab's stuck chips (useFocusCard scrolls to this id).
      id={`ta-card-${candidate.opportunityId}`}
      tabIndex={-1}
      className={`flex items-center gap-3 rounded-xl bg-white px-3 py-2 shadow-sm ring-1 ${
        highlighted ? 'ring-2 ring-blue-400' : 'ring-gray-200'
      } ${handledLabel ? 'opacity-45' : ''}`}
    >
      {handledLabel ? (
        <span className="w-5" aria-hidden />
      ) : (
        <Checkbox checked={checked} onCheckedChange={onToggle} aria-label={`Select ${candidate.name}`} />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-semibold text-gray-900">{candidate.name}</span>
          <a
            href={candidate.leverUrl}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 text-xs text-blue-600 hover:underline"
            title="Open in Lever"
          >
            ↗ Lever
          </a>
          {!candidate.email && (
            <span
              className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold"
              style={{ backgroundColor: COLORS.redBg, color: COLORS.redText }}
              title="No email address in Lever — this candidate cannot be invited by mail"
            >
              no email
            </span>
          )}
        </div>
        <div className="truncate text-xs text-gray-500">
          <span style={stale ? { color: COLORS.redText, fontWeight: 600 } : undefined}>
            {fmtDaysAgo(candidate.waitingDays, 'waiting since')}
          </span>
          {candidate.trail.length > 0 && (
            <span title="Already interviewed — they reached this stage without being invited to it">
              {' '}
              · {candidate.trail.length} interview(s) on record
            </span>
          )}
        </div>
      </div>
      <CandidateChips candidateName={candidate.name} opportunityId={candidate.opportunityId} />
      <PhoneChip phone={candidate.phone} size="sm" />
      {handledLabel && <HandledChip label={handledLabel} />}
    </div>
  );
}
